import { useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import { useStaffMemberInfoContext } from '@/pages/StaffMemberInfo/StaffMemberInfo.context';
import candidateEducation, { DeleteEducationType } from '@/services/candidate-education';
import staffMemberServices, {
  DeleteJobInfoProps,
  RemoveDirectReportProps,
} from '@/services/staffMember';
import teamsApi from '@/services/teams';
import { FetchApiIds } from '@/types';
import { onScrollToSmoothly } from '@/utils/global';
import StaffMemberJobSetcionComponent, {
  Props as StaffMemberJobSetcionComponentProps,
  Tab,
  NEW_JOB_FORM_ID,
} from './StaffMemberJobSection.component';
import DialogBox from '../DialogBox';

type Props = Pick<StaffMemberJobSetcionComponentProps, 'id' | 'staffMemberId'>;

const StaffMemberJobSetcion = ({ staffMemberId, ...props }: Props): JSX.Element => {
  const [tab, setTab] = useState<Tab>('Summary');
  const [addJobInfo, setAddJobInfo] = useState<boolean>(false);
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [jobsIdToDelete, setJobsIdToDelete] = useState('');

  const deleteJobInfo = useApiSubmit<never, DeleteJobInfoProps>(staffMemberServices.deleteJobInfo);

  const toast = useToastContext();

  const { directLeader } = useStaffMemberInfoContext();

  const { data: teamsData } = useApiFetch([FetchApiIds.GET_ALL_TEAMS], async () =>
    teamsApi.getAllTeams({
      page: 1,
      take: 9999,
    })
  );

  const { data: jobInfos, refetch } = useApiFetch(
    [FetchApiIds.LIST_JOB_INFOS, staffMemberId],
    async () =>
      staffMemberServices.listJobInfos({
        staffMemberId,
      })
  );

  const handleAddJobInfoClick = (): void => {
    setAddJobInfo(true);

    if (tab === 'Summary') {
      setTab('Details');
      setTimeout(() => {
        onScrollToSmoothly(NEW_JOB_FORM_ID);
      }, 100);
    }
  };

  const handleEditClick = (jobId: string): void => {
    setTab('Details');
    setEditing((values) => ({
      ...values,
      [jobId]: true,
    }));
  };

  const onDeleteJob = async (jobId: string): Promise<void> => {
    try {
      await deleteJobInfo.mutateAsync({
        staffMemberId,
        jobInfoId: jobId,
      });
      toast.showSuccessFeedback('The job has been deleted.');

      refetch();

      setJobsIdToDelete('');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the job.');
    }
  };

  const openDeleteDialogJob = (jobId: string): void => {
    setJobsIdToDelete(jobId);
  };

  const handleEditCancelClick = (jobId: string): void => {
    setEditing((values) => ({
      ...values,
      [jobId]: false,
    }));
  };

  const handleAddNewJob = (): void => {
    refetch();
    setAddJobInfo(false);
  };

  const handleChangeTab = (newTab: Tab): void => {
    setTab(newTab);

    if (newTab === 'Summary') {
      setEditing({});
      setAddJobInfo(false);
    }
  };

  const handleUpdateJob = (jobId: string): void => {
    refetch();
    setEditing((values) => ({
      ...values,
      [jobId]: false,
    }));
  };

  return (
    <>
      <StaffMemberJobSetcionComponent
        tab={tab}
        onChangeTab={handleChangeTab}
        directLeader={directLeader}
        teams={teamsData?.teams.data}
        staffMemberId={staffMemberId}
        onAddJobInfoClick={handleAddJobInfoClick}
        addingJobInfo={addJobInfo}
        onAddCancel={() => setAddJobInfo(false)}
        editing={editing}
        onEditClick={handleEditClick}
        onEditCancel={handleEditCancelClick}
        jobInfos={jobInfos}
        onAddNewJob={handleAddNewJob}
        onUpdateJob={handleUpdateJob}
        onDeleteJob={openDeleteDialogJob}
        onUpdateReporters={refetch}
        {...props}
      />
      <DialogBox
        isOpen={!!jobsIdToDelete}
        onCancelHandler={() => setJobsIdToDelete('')}
        onConfirmHandler={() => onDeleteJob(jobsIdToDelete)}
      />
    </>
  );
};

export default StaffMemberJobSetcion;
